import { Alert, AlertIcon, Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';
import { FaBars, FaEye, FaSort } from 'react-icons/fa';

import AvailablePreviewList from '@/components/available-preview-list/AvailablePreviewList';
import AdvancedSorting from '@/components/view-editor/ViewLayoutAdvancedSorting';
import ViewLayoutDisplayRange from '@/components/view-editor/ViewLayoutDisplayRange';
import ViewLayoutGroupBy from '@/components/view-editor/ViewLayoutGroupBy';
import ViewLayoutShiftsToDisplay from '@/components/view-editor/ViewLayoutShiftsToDisplay';
import { FEATURE_FLAGS_ENUM, LAYOUT_LIST_PUBLIC_ONLY_COLUMNS } from '@/constants/config';
import { DISPLAY_SHIFT_OPTIONS, ORDINAL_ZERO } from '@/constants/defaults';
import { ViewEditorListViewColumns } from '@/constants/enums';
import useCustomColumnConfig from '@/hooks/useCustomColumnConfig';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setViewEditorAdvancedSortingRulesIsVisible,
  setViewEditorDraftMultiColumnSort,
  setViewEditorDraftThemeDataLayoutListColumns,
} from '@/store/slices/viewEditor.slice';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';
import { UniqueId } from '@/types/ui.types';
import { ShiftToDisplay, ViewLayoutListColumns } from '@/types/view.types';
import { tableColumnFilterFn } from '@/utils/filterFns';

const ADVANCED_SORTING_RULES_INFO_TEXT =
  'This view is using legacy sorting that can no longer be edited. Please use the new Advanced Sorting Rules section to update the sort order of this view';

interface ViewLayoutListProps {
  displayRange: string;
  shiftsToDisplay: string[];
  groupBy: string;
  handleDisplayRangeChange: (value: string) => void;
  handleGroupByChange: (value: string) => void;
  handleShiftsToDisplayChange: (value: ShiftToDisplay[]) => void;
  shiftsToDisplayError?: string;
}

interface ListColumnData {
  id: UniqueId;
  name: string;
}

const ViewLayoutList = (props: ViewLayoutListProps): React.JSX.Element => {
  const {
    displayRange,
    shiftsToDisplay,
    shiftsToDisplayError,
    groupBy,
    handleDisplayRangeChange,
    handleShiftsToDisplayChange,
    handleGroupByChange,
  } = props;
  const selectedRange = ['day', 'week'].includes(displayRange) ? displayRange : 'day';

  const flags = useFlags();
  const shiftsToDisplayFeatureEnabled = flags[FEATURE_FLAGS_ENUM.LV14123DailyListViewHideSections];

  const {
    uiState: { advancedSortingRulesEnabled, showAdvancedSortingRules },
    viewDraft: view,
    viewOriginal,
  } = useAppSelector((state) => state.viewEditor);

  const { theme } = view;

  const multiSortColumnsEnabled = React.useMemo(
    () =>
      viewOriginal &&
      viewOriginal.theme?.data?.multiSortColumns &&
      Boolean(viewOriginal.theme?.data?.multiSortColumns.length),
    [viewOriginal],
  );

  const dispatch = useAppDispatch();

  const { useCustomColumnDisplayName, customColumnDisplayNames } = useCustomColumnConfig();
  const [custom1Name, custom2Name, custom3Name, custom4Name] = customColumnDisplayNames;

  const columnIdMap = React.useMemo(() => {
    const customDisplayNames: Partial<Record<ViewLayoutListColumns, string>> = !useCustomColumnDisplayName ? {} : {
      ...(custom1Name && { [ViewLayoutListColumns.CUSTOM_1]: custom1Name }),
      ...(custom2Name && { [ViewLayoutListColumns.CUSTOM_2]: custom2Name }),
      ...(custom3Name && { [ViewLayoutListColumns.CUSTOM_3]: custom3Name }),
      ...(custom4Name && { [ViewLayoutListColumns.CUSTOM_4]: custom4Name }),
    };

    const map = new Map<string, ListColumnData>();

    Object.values(ViewLayoutListColumns).forEach((column) => {
      map.set(column, { id: column, name: customDisplayNames[column] ?? column });
    });

    return map;
  }, [useCustomColumnDisplayName, custom1Name, custom2Name, custom3Name, custom4Name]);

  const currentSelectedColumns = React.useMemo(() => view.theme.data.listColumns ?? [], [view.theme.data.listColumns]);

  const [availableItems, previewItems] = React.useMemo(() => {
    const availableItemsMap = new Map<string, ListColumnData>();
    const previewItemsMap = new Map<string, ListColumnData>();

    // Hydrate available items, order does not matter
    for (const [k, v] of columnIdMap.entries()) {
      if (!currentSelectedColumns?.includes(v.id as ViewLayoutListColumns)) {
        availableItemsMap.set(k, v);
      }
    }

    // Hydrate preview items, order DOES matter
    currentSelectedColumns.forEach((columnName) => {
      if (columnIdMap.has(columnName)) {
        previewItemsMap.set(columnName, columnIdMap.get(columnName) as ListColumnData);
      }
    });

    return [availableItemsMap, previewItemsMap];
  }, [columnIdMap, currentSelectedColumns]);

  const availableColumnsHelper = createColumnHelper<ListColumnData>();

  const injectCellContent = (item: CellContext<ListColumnData, string>) => {
    if (LAYOUT_LIST_PUBLIC_ONLY_COLUMNS.includes(item.row.original.id as ViewLayoutListColumns)) {
      return <Icon as={FaEye} margin={'0 4px'} fontSize={'xl'} verticalAlign={'center'} />;
    }

    return <></>;
  };

  const availableItemsColumns = [
    // eslint-disable-next-line react/prop-types
    availableColumnsHelper.accessor((row) => row.name, {
      enableSorting: true,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: ViewEditorListViewColumns.name,
    }),
  ];

  const previewColumnsHelper = createColumnHelper<ListColumnData>();

  const previewColumns = [
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.name, {
      enableSorting: false,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: ViewEditorListViewColumns.name,
    }),
  ];

  const handleUpdateSelectedItems = React.useCallback(
    (items: ListColumnData[]) => {
      const previewColumnsNames = items.map((item) => item.id as string);
      dispatch(setViewEditorDraftThemeDataLayoutListColumns(previewColumnsNames));

      // if the user deselects a column that has already been set as a rule in the Advanced Sorting Rules ,
      // discard it from the rule list
      const existingRules = theme.data.multiSortColumns;
      if (!existingRules?.length) return;

      // only keep the rules that are in the selected columns table
      const updatedRules = existingRules?.filter(({ columnValue }) => previewColumnsNames.includes(columnValue));

      if (!_.isEqual(existingRules, updatedRules)) {
        dispatch(setViewEditorDraftMultiColumnSort(updatedRules));
      }
    },
    [dispatch, theme.data.multiSortColumns],
  );

  return (
    <VStack align={'left'} gap={5}>
      <HStack alignItems={'start'} gap={10} justifyContent={'start'}>
        <ViewLayoutDisplayRange
          onChange={handleDisplayRangeChange}
          options={[
            { label: 'Day', value: 'day' },
            { label: 'Week', value: 'week' },
          ]}
          selected={selectedRange}
        />

        {!advancedSortingRulesEnabled && <ViewLayoutGroupBy onChange={handleGroupByChange} selected={groupBy} />}

        {shiftsToDisplayFeatureEnabled && (
          <ViewLayoutShiftsToDisplay
            errorMessage={shiftsToDisplayError}
            onChange={handleShiftsToDisplayChange}
            options={DISPLAY_SHIFT_OPTIONS}
            selected={shiftsToDisplay}
          />
        )}
      </HStack>

      {advancedSortingRulesEnabled && previewItems.size > ORDINAL_ZERO && (
        <Box display={'flex'} justifyContent={'end'}>
          {!multiSortColumnsEnabled && (
            <Text color="red" align="left" fontSize="md">
              {ADVANCED_SORTING_RULES_INFO_TEXT}
            </Text>
          )}

          <Button
            data-testid={ELEMENT_DATA_TEST_IDS.SHOW_ADVANCED_SORTING_RULES}
            w={250}
            leftIcon={showAdvancedSortingRules ? <FaBars /> : <FaSort />}
            colorScheme="blue"
            onClick={() => dispatch(setViewEditorAdvancedSortingRulesIsVisible(!showAdvancedSortingRules))}
          >
            {showAdvancedSortingRules ? 'Columns Configuration' : 'Advanced Sorting Rules'}
          </Button>
        </Box>
      )}

      <Alert status="info">
        <AlertIcon />
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <p>For publicly accessible list views, only those columns marked with</p>
          <Icon as={FaEye} margin={'0 4px'} fontSize={'xl'} verticalAlign={'center'} />
          <p>will be visible.</p>
        </HStack>
      </Alert>

      <VStack gap={5} align={'top'}>
        {showAdvancedSortingRules ? (
          <AdvancedSorting />
        ) : (
          <AvailablePreviewList
            availableColumns={availableItemsColumns}
            availableItems={availableItems}
            availableItemsPrimaryColumnId={'name'}
            availableItemsTableLabel={'Available Columns'}
            availableRowUniqueKey={'id'}
            cellContentInjector={injectCellContent}
            isOpen={true}
            onItemsChanged={handleUpdateSelectedItems}
            overrideSorting={true}
            previewColumns={previewColumns}
            previewItems={previewItems}
            previewItemsPrimaryColumnId={'name'}
            previewItemsTableLabel={'Selected Columns'}
            previewRowUniqueKey={'id'}
            primaryAvailableColumnIndex={0}
            primaryPreviewColumnIndex={0}
            showSideControls={true}
            tableColumnIds={ViewEditorListViewColumns}
          />
        )}
      </VStack>
    </VStack>
  );
};

export default ViewLayoutList;
